import { Attachments } from '../attachments/Attachments'
// A plan's page (today's doc/DocumentPage.tsx, for a plan): its title, its
// summary, status, progress and claim, its body, and its comments. An HTML
// plan is drawn as its page (PlanView), with its side menu (PlanNav) in the
// outline's place; a Markdown plan as its text, its keys linked with their
// live states. The row comes from the store's snapshot and feed; the body,
// its revisions and its comments from `GET /v1/plans/{id}`, which the store
// reads while the page watches the plan, and again when its revision moves.
// Each phase with work has a Dispatch button: it opens the New session dialog
// filled for a dispatcher on that phase (dispatch.ts), and the session started
// opens in the Code tab. An archived plan's phases have none.
// Today's `/doc/<id>` of a plan leads here (`OldPlanAddress`, links/OldLink.tsx). Revisions,
// attachments and archiving are left out.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import type { PlanDetail } from '../api/types'
import { PlanIcon } from '../components/Icons'
import { InlineText } from '../components/InlineText'
import { ErrorState, Loading, NotFound, Section } from '../components/layout'
import { useDocumentTitle } from '../components/useDocumentTitle'
import { NewSessionDialog } from '../code/NewSession'
import { planPath, projectPath, sessionPath } from '../projects/routing'
import { useWorkspaceDraft } from '../workspace/drafts'
import { dispatchPrompt, dispatchTitle } from './dispatch'
import { useBoardState, useStore } from '../store/context'
import { cx } from '../transcript/bits'
import { isHtmlBody } from './format'
import { parsePlanHtml } from './parse'
import { MarkdownPlan, PlanBody } from './PlanBody'
import { PlanComments } from './PlanComments'
import { PlanHeader } from './PlanHeader'
import { PlanKeysProvider, usePlanKeys } from './html'
import { PlanNav } from './PlanNav'
import { PlanView } from './PlanView'
import { listedIssues } from './state'
import { pageWords as w } from './words'
import './plans.css'

/** The store's read of plan `id` while the page shows it. */
function useWatchedPlan(id: string) {
  const store = useStore()
  const { planDetails, planErrors } = useBoardState()
  useEffect(() => store.watchPlan(id), [store, id])
  return { detail: planDetails.get(id), error: planErrors.get(id), retry: () => void store.refreshPlan(id) }
}

export function PlanPage() {
  const { id = '', project: key = '' } = useParams()
  const { projects, plans } = useBoardState()
  const { detail, error, retry } = useWatchedPlan(id)
  const plan = plans.get(id) ?? detail
  const owner = plan ? projects.get(plan.project) : undefined
  useDocumentTitle(plan?.title ?? w.plan)
  if (!detail && error) return error.status === 404 ? <NotFound what="plan" /> : <ErrorState error={error} onRetry={retry} />
  if (!plan) return <Loading />
  // A plan is its project's: at another project's address, it moves to its own.
  if (owner && owner.key.toLowerCase() !== key.toLowerCase()) return <Navigate to={planPath(owner.key, id)} replace />
  if (!detail) return <Loading />
  // Keyed, so an edit under way never follows to another plan.
  return <PlanDocument key={id} detail={detail} projectKey={owner?.key ?? key} />
}

function PlanDocument({ detail, projectKey }: { detail: PlanDetail; projectKey: string }) {
  const store = useStore()
  const { issues, projects, plans, offline } = useBoardState()
  const navigate = useNavigate()
  const live = plans.get(detail.id)
  const plan = live ? { ...detail, ...live, body: detail.body, revision: detail.revision } : detail
  const [editing, setEditing] = useState(false)
  const names = useMemo(() => [...projects.values()].map((project) => project.key), [projects])
  const html = isHtmlBody(plan.body)
  const model = useMemo(() => (html ? parsePlanHtml(plan.body, names) : null), [html, plan.body, names])
  const listed = useMemo(() => listedIssues(plan, issues), [plan, issues])
  const keys = usePlanKeys(issues, names)
  const withNav = model !== null && model.diagnostics.length === 0 && !editing
  const onEditingChange = useCallback((value: boolean) => setEditing(value), [])
  // The feed says the body moved on: the page shows the body it read until the store has read the new one.
  const stale = live !== undefined && live.revision > detail.revision
  // The phase a dispatcher is being started on, by its anchor: the dialog stays open across a reload while the phase is there.
  const [dispatching, setDispatching] = useWorkspaceDraft<string | null>(`plan.${plan.id}.dispatch`, null, isPhaseId, () => false)
  const phase = dispatching && model ? model.phases.find((p) => p.id === dispatching) : undefined
  const onDispatch = useCallback((p: { id: string }) => setDispatching(p.id), [setDispatching])

  return (
    <article className={cx('doc-page', withNav && 'has-outline')}>
      {withNav && model ? <PlanNav model={model} listed={listed} issues={issues} /> : null}
      <div className="doc-main">
        <header className="doc-head">
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <Link to={projectPath(projectKey, '/plans')}>{w.title}</Link>
          </nav>
          <p className="doc-kind">
            <PlanIcon size={14} />
            {w.plan}
            {plan.legacy_ref && (
              <span className="legacy-ref" title={w.formerNumber}>
                {plan.legacy_ref}
              </span>
            )}
          </p>
          <InlineText
            as="h1"
            className="doc-title"
            value={plan.title}
            onSave={(title) => void store.updatePlan(plan.id, { title })}
            placeholder={w.untitled}
            name="title"
            maxLength={500}
            required
            multiline
          />
          <PlanHeader plan={plan} projectKey={projectKey} />
          {plan.archived_at && <p className="notice">{w.archived}</p>}
        </header>
        <Section title={w.plan}>
          {stale && <p className="muted" role="status">{w.loadingPlan}</p>}
          <PlanBody
            plan={plan}
            onEditingChange={onEditingChange}
            view={
              model ? (
                <PlanView planId={plan.id} model={model} listed={listed} issues={issues} names={names} onDispatch={plan.archived_at ? undefined : onDispatch} offline={offline} />
              ) : (
                <PlanKeysProvider value={keys}>
                  <MarkdownPlan source={plan.body} />
                </PlanKeysProvider>
              )
            }
          />
        </Section>
        <Attachments target={{ plan: plan.id }} />
        <PlanComments planId={plan.id} comments={plan.comments ?? []} />
      </div>
      {phase && (
        <NewSessionDialog
          projectId={plan.project}
          seed={{ prompt: dispatchPrompt(plan, phase.title), title: dispatchTitle(phase.title) }}
          onClose={() => setDispatching(null)}
          onStarted={(session) => {
            setDispatching(null)
            navigate(sessionPath(projectKey, session.id))
          }}
        />
      )}
    </article>
  )
}

const isPhaseId = (value: unknown): value is string | null => value === null || typeof value === 'string'

/** Today's address of a plan, `/doc/<id>`: its page in its project, once the store knows the plan. */
export function OldPlanAddress({ id: given }: { id?: string } = {}) {
  const params = useParams()
  const { search, hash } = useLocation()
  const id = given ?? params.id ?? ''
  const { plans, projects } = useBoardState()
  const { detail, error, retry } = useWatchedPlan(id)
  const plan = plans.get(id) ?? detail
  const project = plan ? projects.get(plan.project) : undefined
  // The address's query and fragment (a comment's anchor) go along.
  if (project && plan) return <Navigate to={planPath(project.key, plan.id) + search + hash} replace />
  return error ? error.status === 404 ? <NotFound what="plan" /> : <ErrorState error={error} onRetry={retry} /> : <Loading />
}
